import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, TrendingUp, Clock, Plus, Loader2, Sparkles, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GanttTimeline = ({ project }) => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [generating, setGenerating] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [project.id]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/gantt-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/timeline-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const generateTasks = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/projects/${project.id}/gantt-tasks/generate`);
      await fetchTasks();
      await fetchStats();
    } catch (error) {
      console.error('Error generating tasks:', error);
    } finally {
      setGenerating(false);
    }
  };

  const createTask = async (taskData) => {
    try {
      const newTask = {
        ...taskData,
        project_id: project.id,
        start_date: new Date(taskData.start_date).toISOString(),
        end_date: new Date(taskData.end_date).toISOString(),
      };
      await axios.post(`${API}/gantt-tasks`, newTask);
      await fetchTasks();
      await fetchStats();
      setShowCreateTask(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const updateTaskProgress = async (taskId, newProgress) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      let newStatus = task.status;
      if (newProgress === 100) newStatus = 'completed';
      else if (newProgress > 0) newStatus = 'in_progress';
      else newStatus = 'not_started';

      await axios.put(`${API}/gantt-tasks/${taskId}`, {
        ...task,
        progress: newProgress,
        status: newStatus
      });
      await fetchTasks();
      await fetchStats();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const phases = ['all', 'discovery', 'analysis', 'design', 'build', 'test', 'deploy'];
  const filteredTasks = selectedPhase === 'all' 
    ? tasks 
    : tasks.filter(t => t.phase === selectedPhase);

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'discovery': return 'bg-blue-500';
      case 'analysis': return 'bg-purple-500';
      case 'design': return 'bg-pink-500';
      case 'build': return 'bg-green-500';
      case 'test': return 'bg-orange-500';
      case 'deploy': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Calculate timeline view
  const getTimelinePosition = (startDate, endDate) => {
    if (!tasks.length) return { left: 0, width: 0 };

    const allDates = tasks.flatMap(t => [new Date(t.start_date), new Date(t.end_date)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    const totalDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);

    const taskStart = new Date(startDate);
    const taskEnd = new Date(endDate);
    const daysFromStart = (taskStart - minDate) / (1000 * 60 * 60 * 24);
    const taskDuration = (taskEnd - taskStart) / (1000 * 60 * 60 * 24);

    return {
      left: `${(daysFromStart / totalDays) * 100}%`,
      width: `${(taskDuration / totalDays) * 100}%`
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Gantt Timeline</h2>
            <p className="text-gray-600">Visual timeline and project schedule management</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={generateTasks}
              disabled={generating}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>AI Generate</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowCreateTask(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Task</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{stats.total_tasks}</span>
            </div>
            <p className="text-blue-100 text-sm font-medium">Total Tasks</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{stats.total_duration_days}</span>
            </div>
            <p className="text-purple-100 text-sm font-medium">Days Total</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{Math.round(stats.completion_percentage)}%</span>
            </div>
            <p className="text-green-100 text-sm font-medium">Complete</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{stats.critical_path_tasks}</span>
            </div>
            <p className="text-orange-100 text-sm font-medium">Critical Path</p>
          </div>
          <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 opacity-80" />
              <span className="text-3xl font-bold">{stats.milestones}</span>
            </div>
            <p className="text-pink-100 text-sm font-medium">Milestones</p>
          </div>
        </div>
      )}

      {/* Phase Filter */}
      <div className="bg-white rounded-xl shadow-md p-4 border border-gray-100">
        <div className="flex items-center space-x-2 overflow-x-auto">
          {phases.map((phase) => (
            <button
              key={phase}
              onClick={() => setSelectedPhase(phase)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-all whitespace-nowrap ${
                selectedPhase === phase
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {phase === 'all' ? 'All Phases' : phase}
            </button>
          ))}
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
          <Calendar className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Timeline Tasks Yet</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">Generate timeline tasks with AI or add them manually</p>
          <button
            onClick={generateTasks}
            disabled={generating}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>Generate Timeline</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Gantt Chart View */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Timeline View</h3>
            <div className="space-y-2">
              {filteredTasks.map((task) => {
                const position = getTimelinePosition(task.start_date, task.end_date);
                return (
                  <div key={task.id} className="relative">
                    <div className="flex items-center space-x-3 mb-1">
                      <div className="w-48 text-sm font-medium text-gray-900 truncate">
                        {task.task_name}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-lg h-8 relative">
                        <div
                          className={`absolute top-0 h-8 ${getPhaseColor(task.phase)} rounded-lg flex items-center px-3 shadow-sm transition-all`}
                          style={position}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="text-xs font-semibold text-white truncate">
                              {task.progress}%
                            </span>
                            {task.is_milestone && (
                              <span className="ml-2">🏁</span>
                            )}
                            {task.is_critical_path && (
                              <span className="ml-2">⚡</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="w-32 text-xs text-gray-500">
                        {new Date(task.start_date).toLocaleDateString()}
                      </div>
                      <div className="w-20">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                          {task.status === 'not_started' ? 'Pending' : 
                           task.status === 'in_progress' ? 'Active' : 
                           task.status === 'completed' ? 'Done' : task.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task List View */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Task Details</h3>
            <div className="space-y-3">
              {filteredTasks.map((task) => (
                <div key={task.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{task.task_name}</h4>
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold text-white ${getPhaseColor(task.phase)}`}>
                          {task.phase}
                        </span>
                        {task.is_milestone && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-semibold">
                            🏁 Milestone
                          </span>
                        )}
                        {task.is_critical_path && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold">
                            ⚡ Critical
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                      <div className="flex items-center space-x-6 text-xs text-gray-500">
                        <span>
                          <span className="font-semibold">Start:</span> {new Date(task.start_date).toLocaleDateString()}
                        </span>
                        <span>
                          <span className="font-semibold">End:</span> {new Date(task.end_date).toLocaleDateString()}
                        </span>
                        <span>
                          <span className="font-semibold">Duration:</span> {task.duration_days} days
                        </span>
                        {task.assigned_to && (
                          <span>
                            <span className="font-semibold">Assigned:</span> {task.assigned_to}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`ml-4 px-3 py-1 rounded-lg text-sm font-semibold ${getStatusColor(task.status)}`}>
                      {task.status === 'not_started' ? 'Not Started' : 
                       task.status === 'in_progress' ? 'In Progress' : 
                       task.status === 'completed' ? 'Completed' : task.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">Progress</span>
                      <span className="font-bold text-gray-900">{task.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`${getPhaseColor(task.phase)} h-3 rounded-full transition-all`}
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={task.progress}
                      onChange={(e) => updateTaskProgress(task.id, parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Create Task Modal */}
      {showCreateTask && (
        <CreateTaskModal onClose={() => setShowCreateTask(false)} onCreate={createTask} />
      )}
    </div>
  );
};

// Create Task Modal
const CreateTaskModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    task_name: '',
    description: '',
    phase: 'build',
    start_date: '',
    end_date: '',
    duration_days: 7,
    assigned_to: '',
    is_milestone: false,
    is_critical_path: false,
    progress: 0,
    status: 'not_started',
    dependencies: [],
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Create Timeline Task</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task Name *</label>
            <input
              type="text"
              required
              value={formData.task_name}
              onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., System configuration"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phase</label>
              <select
                value={formData.phase}
                onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="discovery">Discovery</option>
                <option value="analysis">Analysis</option>
                <option value="design">Design</option>
                <option value="build">Build</option>
                <option value="test">Test</option>
                <option value="deploy">Deploy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (days)</label>
              <input
                type="number"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date *</label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">End Date *</label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Assigned To</label>
            <input
              type="text"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Team member name"
            />
          </div>
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_milestone}
                onChange={(e) => setFormData({ ...formData, is_milestone: e.target.checked })}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-gray-700">Mark as Milestone 🏁</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_critical_path}
                onChange={(e) => setFormData({ ...formData, is_critical_path: e.target.checked })}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-gray-700">Critical Path ⚡</span>
            </label>
          </div>
          <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GanttTimeline;